import React, { useRef } from "react";
import { Typography, useTheme } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import Divider from "@mui/material/Divider";
import "../JaldiLab.css";
import childFriendlyPOCSO from "../../../assets/Images/VWDC/Signage 3_0 warangal POCSO.webp";
import {
  referenceHtml,
  referenceTootKit,
  h1style,
  h2style,
  h3style,
  paraStyle,
  h4style,
  sectionStyle,
  DividerBorderStyle,
  getTextContentHeading,
  getCircleTypeListItem,
  tableCaption,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import { Table7, Table8 } from "./VWDCTables/VWDCTables";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import tactile from "../../../assets/Images/tactile.jpg";
import sensoryBoard from "../../../assets/Images/Sensory_board.jpeg";
import prototypePage from "../../../assets/Images/VWDC/Prototype_Page.jpg";
import childPlaying from "../../../assets/Images/VWDC/childPlaying.png";
import childProtectionSeat from "../../../assets/Images/VWDC/childProtectionSeat.jpeg";
import renderPoco from "../../../assets/Images/VWDC/Renders_PocsoCourtroom.png";
import { FigureDesc44, ImageDesc57 } from "../../../utils/tmp/ImageDescription/VwdcID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};
const VWDC = ({ goToSection }) => {
  const referencesRef = useRef(null);
  const tableHeading = {
    color: "#000",
    fontSize: "1rem",
    fontWeight: 600,
    paddingRight: "5px",
  };

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content:
        "Witness Deposition Committee of Supreme Court of India, ‘Guidelines for Recording of Evidence of Vulnerable Witnesses’ (2022) [“VWDC Guidelines”]",
    },
    {
      content: (
        <>
          <i>Smruti Tukaram Badade v. State of Maharashtra,</i> 2019 SCC OnLine
          SC 2122.{" "}
        </>
      ),
    },
    {
      content: "VWDC Guidelines.",
    },
    {
      content: (
        <>
          Dr. Francis Wardle, ‘Creating indoor environments for young children’
          (<i>Community Playthings,</i> June 2010){" "}
        </>
      ),
      link: "https://www.communityplaythings.co.uk/learning-library/articles/creating-indoor-environments-for-young-children",
      description:
        "<https://www.communityplaythings.co.uk/learning-library/articles/creating-indoor-environments-for-young-children> [“Indoor Environments for Young Children”]",
    },
    {
      content: (
        <>
          ‘Late teacher’s dream for sensory room comes true’ (<i>KION46,</i> 8
          September 2021){" "}
        </>
      ),
      link: "https://kion546.com/cnn-regional/2021/09/08/late-teachers-dream-for-sensory-room-comes-true/",
      description:
        "<https://kion546.com/cnn-regional/2021/09/08/late-teachers-dream-for-sensory-room-comes-true/> [“Weeping Water Elementary School, Nebraska, US”]",
    },

    { content: "NCMS (2012). " }, //5
  ];

  const ObjectivesObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Ensure that the design of the VWDC{" "}
        <span style={{ color: "#DC002D" }}>
          facilitates the Vulnerable Witnesses to depose freely{" "}
        </span>{" "}
        before any Court in a comfortable, accessible, safe, and secure
        environment;
      </>,
      <>
        {" "}
        <span style={{ color: "#DC002D" }}>
          Ensure that the Vulnerable Witnesses face minimal harm{" "}
        </span>
        and/or secondary victimisation when participating in the justice system;
        and
      </>,
      <>
        Although VWDCs are meant to primarily safeguard the interests of
        Vulnerable Witnesses,
        <span style={{ color: "#DC002D" }}>
          {" "}
          ensure that the rights of the Accused to a fair trial shall not be
          impinged,{" "}
        </span>{" "}
        and separate provisions for their effective participation are made so
        that the Accused and the Vulnerable Witness do not come into contact
        with each other.{reference(referenceData[2], 3)}
      </>,
    ],
    isImage: true,
  };

  const UsersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Vulnerable witnesses",
      "Family members",
      "Support persons",
      "Interpreters/translators",
      "Judges",
      "Advocates",
    ],
    isOrdered: true,
    isImage: true,
  };

  const AdjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The VWDC must be located in a separate part of the Court Complex, in proximity to one of the entry points, to ensure easy access for Vulnerable Witnesses. The location must also be determined keeping in mind the safety of the witness and the provision to ensure segregated paths for the witness and the Accused.",
      "Where the above is not possible, the Vulnerable Witness space must be planned to be acoustically isolated from the other common areas to prevent any noise intrusions and to ensure comfort and privacy for the witnesses. ",
      "The VWDC must be adjacent to a green space to ensure that there is a space for the users to relax. The green space may also be equipped with outdoor play equipment, for such instances where the Vulnerable Witness is a child.",
      "The VWDC must be close to a parking area designated for Vulnerable Witnesses and accessed from the parking by a segregated path.",
      "The VWDC must be located close to a designated and secure lift/staircase leading to the Courtroom.",
    ],
    isImage: true,
  };

  const additionallyItem = [
    "Management of time of entry to the site and towards the parking area; ",
    "Provision of designated/segregated parking area;",
    "Provision of segregated path from the parking to the building entrance; ",
    "Provision of a designated lift/staircase core for the Vulnerable Witness to access the VWDC/Courtroom, and ensure security; ",
  ];

  const designConsiderObject = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      <>
        The relevant provisions of the guidelines which pertain to
        infrastructural requirements for the VWDCs are:
        <div style={{paddingBottom:"15px"}}>
          <p className="table-guidance-heading">
            <span className="table-guidline-text">Table 7:</span>
            <br />
            <span style={tableCaption}>
              Design considerations arising from the Model Guidelines for VWDCs
              (2022).
            </span>
          </p>

          <Table7 />
        </div>
      </>,
      <>
        The approach to the VWDC must allow the witness to access it with ease
        and without having to confront other litigants, police, or the Accused
        and their associates. Hence, provision may be made to provide a
        segregated path for the Vulnerable Witness keeping in mind the
        following: {getCircleTypeListItem(additionallyItem, true)}
      </>,
      "Greenery, water features, and other soft landscape elements must be provided along the pathways taken by the Vulnerable Witnesses to make it non-intimidating, safe, calm,  and experientially pleasant.",

      "The VWDC must be an all-inclusive and universally accessible space for diverse user groups including elderly, transgenders, Persons with Disabilities, parents attending to their infants and young children, etc.",

      "The design of the VWDC must be holistic, to ensure visual continuity of design through the Courtroom, waiting area, deposition room, and other spaces in the Complex.",

      <>
        The interior design of the VWDC must be contextually sensitive to
        varying cultural nuances related to religion, language, and social
        practices. In such cases where artwork is used in the rooms, the artwork
        provided must be culturally relevant and must not be made using very
        dark colours or too many colours.
      </>,

      "The VWDC must be a self-contained space such that the Vulnerable Witness does not have to step out of the Centre and come in contact with the litigants, police, or the Accused and their associates. Hence, separate sanitary provisions within the VWDC must be provided.",

      "Family-friendly and gender-neutral accessible washrooms must be provided, to cater to a diverse group of users including elderly, transgenders, Persons with Disabilities, parents attending to their infants, young children, etc. ",

      "The inclusive and accessible washrooms must be equipped with a child-sized water closet and wash basin, a diaper changing station, provision seat for nursing mothers, vending machine or provision for sanitary napkins and diapers, grab bars, etc.",

      "The design and location of drinking water units/stalls must be accessible and inclusive to all diverse user groups, through the use of heights, signages to identify the location, floor finish to avoid slippery and water logging issues, and regular checks for maintenance.",

      <>
        Dustbins must be provided for segregating waste and a waste management
        disposal system, including sanitary disposal mechanisms, must be put in
        place to help maintain hygiene and cleanliness. For further details for
        Sanitary Provisions, please refer to{" "}
        <span
          onClick={() =>
            goToSection("sanitaryProvisions", "sanitaryProvisions")
          }
          style={sectionStyle}
        >
          Section 3.1.
        </span>
      </>,

      "The VWDC engages with persons of varied ages, genders and Persons with Disabilities. Each of the users require the design and infrastructure to cater to their needs. For example, youth in the age group of 13 years to 18 years will need to be engaged in the space differently from the children in the age group of 3 years to 5 years to make them feel safe and comfortable. The use of colours, choice of child friendly toys and the design of the spaces need to be responsive to the varied users.",

      "The Court must make provisions for fire safety systems and put in place an emergency evacuation plan, as per norms.",

      <>
        Provision must be made for a Quiet Room within the VWDC or in close
        proximity to it, to ensure that all users have quick access to the room
        if necessary. For further details regarding the design of a Quiet Room,
        please refer to
        <span
          onClick={() => goToSection("vwdc", "vulnerable-witness")}
          style={sectionStyle}
        >
          {" "}
          Section 3.6.
        </span>
      </>,

      <>
        All spaces in the VWDC must be acoustically treated to ensure privacy
        and to facilitate a calming environment for the users. For further
        details on acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          {" "}
          Annexure 5.
        </span>
      </>,

      <>
        Provision should also be made for family friendly facilities including
        Feeding Room within the VWDC or in close proximity to it. For further
        details for family friendly facilities, please refer to{" "}
        <span
          onClick={() =>
            goToSection(
              "familyChildFriendlyServices",
              "familyChildFriendlyFacilities"
            )
          }
          style={sectionStyle}
        >
          {" "}
          Section 3.3.
        </span>
      </>,

      "Adequate, accessible information and signage support, with large and readable fonts, universally recognised symbols/pictograms, and multilingual with adequate colour contrast must be provided in the VWDCs.",
    ],
    isImage: true,
  };

  const AdditionallySubArray = [
    "Management of time of entry to the site and towards the parking area;   ",
    "Provision of designated/ segregated parking area; ",
    "Provision of segregated path from the parking to the building entrance;  ",
    "Provision of a designated lift/staircase core for the Vulnerable Witness to access the VWDC/Courtroom, and ensure security; ",
  ];

  const AdditionallyArray = [
    <>
      The approach to the VWDC must allow the witness to access it with ease and
      without having to confront other litigants, police, or the Accused and
      their associates. Hence, provision may be made to provide a segregated
      path for the Vulnerable Witness keeping in mind the following:
      {getCircleTypeListItem(AdditionallySubArray)}
    </>,
    "Greenery, water features, and other soft landscape elements must be provided along the pathways taken by the vulnerable witnesses to make it non-intimidating, safe, calm,  and experientially pleasant.  ",
    "The VWDC must be an all-inclusive and universally accessible space for diverse user groups including elderly, transgenders, Persons with Disabilities, parents attending to their infants and young children, etc.",
    "The design of the VWDC must be holistic, to ensure visual continuity of design through the Courtroom, waiting area, deposition room, and other spaces in the Complex.   ",
    "The interior design of the VWDC must be contextually sensitive to varying cultural nuances related to religion, language, and social practices. In such cases where artwork is used in the rooms, the artwork provided must be culturally relevant and must not be made using very dark colours or too many colours.   ",
    "The VWDC must be designed in such a way that it is responsive to the diverse needs of users of varied ages, genders, and Persons with Disabilities.   ",
    "Space must be provided inside the Courtroom, as well as video-linkage facilities, for a translator who may assist the vulnerable witness in recording testimony.   ",
    "The VWDC must be a self-contained space such that the vulnerable witness does not have to step out of the Centre and come in contact with the litigants, police, or the accused and their associates. Hence, separate sanitary provisions within the VWDC must be provided.   ",
    "Family-friendly and gender-neutral accessible washrooms must be provided, to cater to a diverse group of users including elderly, transgenders, Persons with Disabilities, parents attending to their infants, young children, etc.   ",
    "The inclusive and accessible washrooms must be equipped with a child-sized water closet and wash basin, a diaper changing station, provision seat for nursing mothers, vending machine or provision for sanitary napkins and diapers, grab bars, etc.",
    "The design and location of drinking water units / stalls must be accessible and inclusive to all diverse user groups, through the use of heights, signages to identify the location, floor finish to avoid slippery and water logging issues, and regular checks for maintenance.   ",
    "Dustbins must be provided for segregating waste and a waste management disposal system, including sanitary disposal mechanisms, must be put in place to help maintain hygiene and cleanliness.   ",
    "The VWDC engages with persons of varied ages, genders and Persons with Disabilities. Each of the users require the design and infrastructure to cater to their needs. For example, youth in the age group of 13 years to 18 years will need to be engaged in the space differently from the children in the age group of 3 years to 5 years to make them feel safe and comfortable. The use of colours, choice of child friendly toys and the design of the spaces need to be responsive to the varied users.",
    "The Court must make provisions for fire safety systems and put in place an emergency evacuation plan, as per norms. ",
    <>
      Provision must be made for a Quiet Room within the VWDC or in close
      proximity to it, to ensure that all users have quick access to the room if
      necessary. For further details regarding the design of a Quiet Room, refer
      to{" "}
      <span
        onClick={() => goToSection("quietSensoryRoom", "HeadingQuietRoom")}
        style={sectionStyle}
      >
        Sections 3.6{" "}
      </span>
    </>,
    "Adequate, accessible information and signage support, with large and readable fonts, universally recognised symbols/pictograms, and multilingual with adequate colour contrast must be provided in the VWDCs. ",
  ];

  const DesignConsiderationsObjectVWDCWaitingArea = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      "The waiting area must be in close proximity to the filing section, e-Seva Kendra, e-kiosks, etc.",
      "The waiting area must have access to washrooms and drinking water for diverse user groups.",
      "Adequate signage including a map of the Court Complex, directions to accessible facilities, and fire exit plans must be provided.",
      <>
        The waiting area must have direct access or be in close proximity to the
        main stairs/fire stairs, etc., for efficient evacuation during any
        emergency.{reference(referenceData[5], 6)}{" "}
      </>,
      "Case display boards and audible announcements must be provided.",
      "The waiting area must be designed keeping in mind the requirements of the diverse users that may use the space, and soothing colours must be used to cater to the needs of all users.",
      <>
        A selection of toys, books, games and other comfort items may be
        provided in the waiting area for such instances where the Vulnerable
        Witness is a child. For further details of trauma-informed design
        including child-friendly design, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("vwdc", "vulnerable-witness")}
        >
          {" "}
          Section 2.3.A
        </span>
      </>,
      "The waiting area must have an adequate number of durable, ergonomic seats, including seating for diverse user groups.",
      "The waiting area may also have artworks in the form of paintings, murals, and sculptures for ensuring an appropriate and soothing internal ambience.",
    ],
    isImage: true,
  };

  const witnessArray = {
    data: [
      "Age and gender-neutral victims of sexual assault;",
      "Witnesses suffering from mental illnesses;",
      "Witnesses under threat perception; and",
      <>
        Witnesses who may be Persons with Disabilities.
        {reference(referenceData[1], 2)}
      </>,
    ],
  };

  const theme = useTheme();

  return (
    <div>
      <Typography>
        <h1 id="theHandbook" style={h1style}>
          PART C - THE HANDBOOK
        </h1>
        <h2 id="courtComplex" style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="VWDC" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.3. </span>
          Vulnerable Witness Deposition Centres
        </h3>{" "}
        <p style={paraStyle}>
          Under the directions of the Hon’ble Supreme Court, all High Courts are
          required to set up at least one Vulnerable Witness Deposition Centre
          (“VWDCs”) in each District Court, to create a safe environment to
          record the statements of the vulnerable victims.
        </p>
        <p style={paraStyle}>
          The Hon’ble Supreme Court also constituted a committee chaired by
          Justice Ms. Gita Mittal, which was tasked with devising and
          implementing an All India VWDC Training Programme and preparing
          guidelines for recording of evidence in the VWDCs. The Guidelines seek
          to improve the response of the justice system towards Vulnerable
          Witnesses, as defined in Section 3(a).{reference(referenceData[0], 1)}
        </p>
        <p style={paraStyle}>
          Additionally, in 2022 the Supreme Court also extended the definition
          of Vulnerable Witnesses to include:
        </p>
        <div style={{ paddingLeft: "20px" }}>
          {getTextContentHeading(witnessArray)}
        </div>
        {getTextContentHeading(ObjectivesObject)}
        {getTextContentHeading(UsersObject)}
        {getTextContentHeading(AdjacenciesObject)}
        {getTextContentHeading(designConsiderObject)}
        <div style={{ paddingTop: "15px" }}>
          <CaptionWithBorders1
            image={[renderPoco]}
            isZoom={true}
            caption="Figure 44: Typical floor plan for a General Waiting Room"
            zoomImgStyle={{ height: "500px" }}
            imageDescription={FigureDesc44}
          />
        </div>
        <DividerBorderStyle />
        <h3 id="vulnerable-witness" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.3.</span>A. Trauma-Informed
          Design Considerations for Vulnerable Witnesses
        </h3>
        <p style={paraStyle}>
          Trauma-informed design and security measures must be approached
          holistically. This involves the design of Court spaces considering
          both physical and psychological safety of all Vulnerable Witnesses
          including children, Persons with Disabilities, elderly persons, etc.,
          Designing spaces that feel open, inviting, and non-threatening and
          non-confrontational while ensuring that security measures are discreet
          and unobtrusive helps strike this balance. Colours,
          {reference(referenceData[3], 4)} textures
          {reference(referenceData[4], 5)} and greenery play an integral role in
          ensuring that a space feels comfortable and secure for all diverse
          user groups.
        </p>
        <p style={paraStyle}>
          It is important to engage with the stakeholders, including architects,
          accessibility consultants, security consultants, mental healthcare
          professionals, and those with lived experiences, to create spaces that
          effectively merge trauma-informed design principles with robust
          security measures.
        </p>
        <p style={paraStyle}>
          Specific design considerations to be kept in mind for Vulnerable
          Witnesses are:
        </p>
        {/*Table 8 */}
        <div style={{ paddingBottom: "5px" }}>
          <p className="table-guidance-heading" style={{ marginTop: "40px" }}>
            <span className="table-guidline-text">Table 8:</span>
            <br />
            <span style={tableCaption}>
              Trauma-informed design considerations for Vulnerable Witnesses.
            </span>
          </p>
          <Table8
            img1={[childPlaying]}
            img2={tactile}
            img3={sensoryBoard}
            img4={childProtectionSeat}
          />
        </div>
        <DividerBorderStyle />
        <h3 id="vwdcwaitingarea" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.3.</span>B. VWDC Waiting Area
        </h3>
        <p style={paraStyle}>
          A segregated waiting area must be provided in the VWDC Complex for the
          users of the space as well as the family members and support persons
          accompanying them.
        </p>
        {getTextContentHeading(DesignConsiderationsObjectVWDCWaitingArea)}
        <p style={{ ...paraStyle, paddingTop: "20px" }}>
          An additional Vulnerable Witness waiting room is necessary to ensure
          that while evidence is being recorded in an ongoing case, there is no
          interruption by the witnesses waiting for upcoming cases. This
          additional waiting room must be separate from waiting areas used by
          other persons visiting the Court.
        </p>
        <CaptionWithBorders1
          image={[prototypePage]}
          caption={"Image 57: Prototype design for a VWDC Waiting Area"}
          creditLabel="Source"
          imageCredit="VWDC Guidelines"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc57}
        />
        <h2 ref={referencesRef} id="vwdcRef" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default VWDC;
